import React, { useEffect, useState } from "react";
import {
  formatCurrencyValueOnBlur,
  sanitizeCurrencyValue,
} from "./currencyUnits";

// Shared editable-number primitives (Phase A promotion, module 11 polish pass).
// CurrencyInput owns money entry; WholeNumberInput/DecimalNumberInput are the
// promoted ChamberSettings count/percent inputs. Read-only currency display
// stays with formatters.ts.

// Verbatim copy of `settingsInputClassName`
// (src/components/pages/MyChamber/ChamberSettings/common/settingsStyles.ts).
// Duplicated here because common/dashboard must not import from
// src/components/pages/** (layering invariant) — keep the two strings in sync.
export const dashboardInputClassName =
  "h-11 rounded-lg border border-[#DCE4F0] bg-white px-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-100 disabled:text-gray-500";

/**
 * Grouped display shape shown while the field is NOT focused: "1,234.00".
 * Display-layer only — the canonical value string never carries commas or "$"
 * (the dollar sign is a visual adornment here, never part of any string).
 * Note: ChamberSettings' legacy formatMoneyValueForDisplay dropped its
 * grouping in the B13 POL-005 fix because there the formatted string IS the
 * stored value; here grouping is safe because it never leaves the display.
 */
const formatGroupedDisplay = (value: string): string => {
  const numeric = Number.parseFloat(sanitizeCurrencyValue(value));
  if (Number.isNaN(numeric)) {
    return "";
  }
  return numeric.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export type CurrencyInputProps = {
  /** Canonical decimal-dollars string, NO "$", NO commas: "", "0", "1234.5", "1234.50" */
  value: string;
  /** Emits sanitized decimal-dollars string on every keystroke; canonical 2-decimal on blur */
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
  className?: string;
  inputClassName?: string;
  adornmentClassName?: string;
  id?: string;
};

export const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onChange,
  placeholder = "0.00",
  disabled = false,
  readOnly = false,
  ariaLabel,
  className = "relative w-full",
  inputClassName = `${dashboardInputClassName} w-full pl-7`,
  adornmentClassName = "pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500",
  id,
}) => {
  const [focused, setFocused] = useState(false);
  // Commas exist only in this display state, never in the emitted `value`.
  const [displayValue, setDisplayValue] = useState<string>(() =>
    formatGroupedDisplay(value)
  );

  useEffect(() => {
    if (!focused) {
      setDisplayValue(formatGroupedDisplay(value));
    }
  }, [value, focused]);

  const handleFocus = () => {
    setFocused(true);
    setDisplayValue(sanitizeCurrencyValue(value));
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeCurrencyValue(event.target.value);
    setDisplayValue(sanitized);
    onChange(sanitized);
  };

  const handleBlur = () => {
    setFocused(false);
    const canonical = formatCurrencyValueOnBlur(displayValue);
    setDisplayValue(formatGroupedDisplay(canonical));
    if (canonical !== value) {
      onChange(canonical);
    }
  };

  return (
    <div className={className}>
      <span aria-hidden="true" className={adornmentClassName}>
        $
      </span>
      <input
        id={id}
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        aria-label={ariaLabel}
        className={inputClassName}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Promoted number inputs (formerly ChamberSettings numericInputs; that file is
// now a re-export shim). Reject non-numeric characters at the input boundary
// (CS-007); values stay strings to match the settings form state.
// ---------------------------------------------------------------------------

export const sanitizeWholeNumberInput = (value: string): string =>
  value.replace(/[^0-9]/g, "");

export type NumberInputProps = {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  ariaLabel?: string;
};

export const WholeNumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  onBlur,
  placeholder,
  className,
  disabled = false,
  readOnly = false,
  ariaLabel,
}) => (
  <input
    inputMode="numeric"
    value={value}
    onChange={(event) => onChange(sanitizeWholeNumberInput(event.target.value))}
    onBlur={onBlur}
    placeholder={placeholder}
    disabled={disabled}
    readOnly={readOnly}
    aria-label={ariaLabel}
    className={className ?? `${dashboardInputClassName} w-full`}
  />
);

export const DecimalNumberInput: React.FC<NumberInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
  readOnly = false,
  ariaLabel,
}) => (
  <input
    inputMode="decimal"
    value={value}
    onChange={(event) => onChange(sanitizeCurrencyValue(event.target.value))}
    placeholder={placeholder}
    disabled={disabled}
    readOnly={readOnly}
    aria-label={ariaLabel}
    className={className ?? `${dashboardInputClassName} w-full`}
  />
);
